
import { useEffect } from 'react';

interface LightboxProps {
  images: { id: string; url: string }[];
  currentIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (index: number) => void;
  onDelete?: (id: string) => void;
}

export default function Lightbox({
  images,
  currentIndex,
  isOpen,
  onClose,
  onNavigate,
  onDelete,
}: LightboxProps) {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && currentIndex > 0) onNavigate(currentIndex - 1);
      if (e.key === 'ArrowRight' && currentIndex < images.length - 1) onNavigate(currentIndex + 1);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, images.length, onClose, onNavigate]);

  if (!isOpen || currentIndex < 0 || currentIndex >= images.length) return null;

  const currentImage = images[currentIndex];

  const handleDelete = () => {
    if (onDelete && confirm('이 사진을 삭제하시겠습니까?')) {
      onDelete(currentImage.id);
      if (currentIndex >= images.length - 1) {
        onNavigate(Math.max(0, currentIndex - 1));
      }
      if (images.length === 1) {
        onClose();
      }
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center">
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-all cursor-pointer z-10"
      >
        <i className="ri-close-line text-2xl"></i>
      </button>

      {/* Delete Button - 관리자 모드일 때만 표시 */}
      {onDelete && (
        <button
          onClick={handleDelete}
          className="absolute top-6 right-24 w-12 h-12 flex items-center justify-center text-white/80 hover:text-red-500 bg-white/10 hover:bg-white/20 rounded-full transition-all cursor-pointer z-10"
        >
          <i className="ri-delete-bin-line text-xl"></i>
        </button>
      )}

      {/* Counter */}
      <div className="absolute top-6 left-6 text-white/80 text-sm font-medium z-10">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Previous Button */}
      {currentIndex > 0 && (
        <button
          onClick={() => onNavigate(currentIndex - 1)}
          className="absolute left-6 w-12 h-12 flex items-center justify-center text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-all cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
      )}

      {/* Next Button */}
      {currentIndex < images.length - 1 && (
        <button
          onClick={() => onNavigate(currentIndex + 1)}
          className="absolute right-6 w-12 h-12 flex items-center justify-center text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-all cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>
      )}

      {/* Image */}
      <div className="max-w-[90vw] max-h-[85vh] flex items-center justify-center">
        <img
          src={currentImage.url}
          alt={`Gallery ${currentIndex + 1}`}
          className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
        />
      </div>
    </div>
  );
}
